import { Injectable } from '@angular/core';
import { MessageService } from 'primeng/api';
import { AuthService } from '../../../services/auth.service';
import { ClientSettingsService } from '../../../services/client-settings.service';
import { FormDataService } from '../../../services/form-data.service';
import { Customer, OfferGroupService } from '../../../services/offer-group.service';

export interface CustomerLookupFormData {
  currentDate: Date | null;
  empCode: string;
  storeCode: string;
  mobileNumber: string;
  customerName: string;
  salesRep: string;
}

/**
 * Shared dependency bag for the customer-lookup cluster (mobile/name inputs,
 * autocomplete filtering, debounced validation, and hold/receipt gating).
 * Component-local UI state is passed through explicitly; true app-wide
 * singletons (FormDataService, ClientSettingsService, AuthService,
 * OfferGroupService, MessageService) are injected directly into this service.
 *
 * `validateCustomerName`/`validateMobileNumber` are callbacks into the
 * component's own delegating methods (not direct sibling calls) so that the
 * debounce setTimeout always re-reads live component state when it fires,
 * instead of the state snapshotted when the keystroke started the timer.
 */
export interface CustomerLookupDeps {
  mobileNumber: string;
  customerName: string;
  customers: Customer[];
  // Live getter (NOT a snapshot): filterCustomers/filterCustomersByMobile read
  // this AFTER `await ensureCustomersLoaded(...)`, so it must reflect the
  // freshly-loaded array rather than whatever `customers` looked like when
  // this deps object was built (before the load finished).
  getCustomers: () => Customer[];
  holdReceiptName: string;
  isMobileMandatory: boolean;
  customerNameValidationTimer: any;
  mobileNumberValidationTimer: any;
  loadingCustomers: boolean;
  formData: CustomerLookupFormData | null;

  setMobileNumber: (value: string) => void;
  setCustomerName: (value: string) => void;
  setCustomers: (value: Customer[]) => void;
  setSelectedCustomer: (value: Customer | null) => void;
  setFilteredCustomers: (value: Customer[]) => void;
  setFilteredCustomersForMobile: (value: Customer[]) => void;
  setCustomerNameValidationTimer: (value: any) => void;
  setMobileNumberValidationTimer: (value: any) => void;
  setLoadingCustomers: (value: boolean) => void;
  setIsMobileMandatory: (value: boolean) => void;
  setDisplayMobileNumberDialog: (value: boolean) => void;
  setDisplayHoldNameDialog: (value: boolean) => void;

  resetLoyaltyData: () => void;
  validateCustomerName: () => void;
  validateMobileNumber: () => void;
}

/**
 * CustomerLookupService
 *
 * Dedicated service for customer name/mobile-number entry, autocomplete
 * filtering, debounced validation against the loaded customer list, and the
 * mobile/receipt gating checks used before proceeding to payment/hold.
 *
 * Extracted from DashboardComponent to decouple customer-lookup logic while
 * preserving 100% identical behavior.
 */
@Injectable({
  providedIn: 'root',
})
export class CustomerLookupService {
  constructor(
    private readonly formDataService: FormDataService,
    private readonly clientSettingsService: ClientSettingsService,
    private readonly authService: AuthService,
    private readonly offerGroupService: OfferGroupService,
    private readonly messageService: MessageService,
  ) {}

  onMobileNumberChange(event: any, deps: CustomerLookupDeps) {
    // Remove any non-numeric characters
    const numericValue = event.target.value.replace(/[^0-9]/g, '');
    deps.setMobileNumber(numericValue);
    event.target.value = numericValue;

    // Reset loyalty data when mobile number is cleared or changed
    if (!numericValue || numericValue.length === 0) {
      deps.resetLoyaltyData();
      deps.setSelectedCustomer(null);
    }

    this.formDataService.updateFormData({
      ...deps.formData,
      mobileNumber: numericValue,
      empCode: deps.formData?.empCode || '',
      storeCode: deps.formData?.storeCode || '',
      currentDate: deps.formData?.currentDate || new Date(),
      customerName: deps.customerName || '',
      salesRep: deps.formData?.salesRep || '',
    });
  }

  handleMobileNumberPaste(event: ClipboardEvent, deps: CustomerLookupDeps) {
    event.preventDefault();
    const pastedText = event.clipboardData?.getData('text') || '';
    const numericValue = pastedText.replace(/[^0-9]/g, '').substring(0, 10);

    deps.setMobileNumber(numericValue);

    const target = event.target as HTMLInputElement;
    if (target) {
      target.value = numericValue;
    }

    // Trigger the change event to update form data
    this.formDataService.updateFormData({
      ...deps.formData,
      mobileNumber: numericValue,
      empCode: deps.formData?.empCode || '',
      storeCode: deps.formData?.storeCode || '',
      currentDate: deps.formData?.currentDate || new Date(),
      customerName: deps.customerName || '',
      salesRep: deps.formData?.salesRep || '',
    });
  }

  onCustomerNameChange(event: any, deps: CustomerLookupDeps) {
    const customerName = event.target.value;
    deps.setCustomerName(customerName);
    this.formDataService.updateFormData({
      ...deps.formData,
      mobileNumber: deps.mobileNumber,
      empCode: deps.formData?.empCode || '',
      storeCode: deps.formData?.storeCode || '',
      currentDate: deps.formData?.currentDate || new Date(),
      customerName: customerName || '',
      salesRep: deps.formData?.salesRep || '',
    });
  }

  // Filter customers for autocomplete
  async filterCustomers(event: any, deps: CustomerLookupDeps) {
    await this.ensureCustomersLoaded(deps);

    const query = event.query.toLowerCase();

    deps.setFilteredCustomers(
      deps.getCustomers().filter(
        (customer) =>
          customer.customer_name.toLowerCase().includes(query) ||
          customer.mobile_no.includes(query),
      ),
    );
  }

  // Filter customers for mobile number autocomplete
  async filterCustomersByMobile(event: any, deps: CustomerLookupDeps) {
    await this.ensureCustomersLoaded(deps);

    const query = event.query.toLowerCase();

    deps.setFilteredCustomersForMobile(
      deps.getCustomers().filter(
        (customer) =>
          customer.mobile_no.includes(query) ||
          customer.customer_name.toLowerCase().includes(query),
      ),
    );
  }

  // Handle customer name model change (when user types)
  onCustomerNameModelChange(value: any, deps: CustomerLookupDeps) {
    if (typeof value === 'object' && value !== null) {
      return;
    }

    // IMPORTANT: Update the customerName property with the string value
    // This ensures the property is always in sync with what the user types
    const customerName = value || '';
    deps.setCustomerName(customerName);

    // Update form data service to persist the change
    this.formDataService.updateFormData({
      ...deps.formData,
      mobileNumber: deps.mobileNumber,
      empCode: deps.formData?.empCode || '',
      storeCode: deps.formData?.storeCode || '',
      currentDate: deps.formData?.currentDate || new Date(),
      customerName: customerName,
      salesRep: deps.formData?.salesRep || '',
    });

    if (deps.customerNameValidationTimer) {
      clearTimeout(deps.customerNameValidationTimer);
    }

    deps.setCustomerNameValidationTimer(
      setTimeout(() => {
        deps.validateCustomerName();
      }, 500),
    );
  }

  // Validate customer name and clear mobile if not found
  validateCustomerName(deps: CustomerLookupDeps) {
    if (deps.customerName && typeof deps.customerName === 'string') {
      const matchingCustomer = deps.customers.find(
        (c) => c.customer_name.toLowerCase() === deps.customerName.toLowerCase(),
      );

      if (!matchingCustomer) {
        // Don't clear mobile number - allow new customer entry
        deps.setSelectedCustomer(null);

        // Update form data service with current values
        this.formDataService.updateFormData({
          ...deps.formData,
          mobileNumber: deps.mobileNumber || '',
          empCode: deps.formData?.empCode || '',
          storeCode: deps.formData?.storeCode || '',
          currentDate: deps.formData?.currentDate || new Date(),
          customerName: deps.customerName || '',
          salesRep: deps.formData?.salesRep || '',
        });
      }
    } else if (!deps.customerName) {
      // Only clear when customer name is empty
      deps.setMobileNumber('');
      deps.setSelectedCustomer(null);
      deps.resetLoyaltyData(); // Reset loyalty data when customer is cleared

      this.formDataService.updateFormData({
        ...deps.formData,
        mobileNumber: '',
        empCode: deps.formData?.empCode || '',
        storeCode: deps.formData?.storeCode || '',
        currentDate: deps.formData?.currentDate || new Date(),
        customerName: '',
        salesRep: deps.formData?.salesRep || '',
      });
    }
  }

  onCustomerNameInputBlur(deps: CustomerLookupDeps) {
    if (deps.customerNameValidationTimer) {
      clearTimeout(deps.customerNameValidationTimer);
    }
  }

  onMobileNumberModelChange(value: any, deps: CustomerLookupDeps) {
    if (typeof value === 'object' && value !== null) {
      return;
    }

    if (deps.mobileNumberValidationTimer) {
      clearTimeout(deps.mobileNumberValidationTimer);
    }

    deps.setMobileNumberValidationTimer(
      setTimeout(() => {
        deps.validateMobileNumber();
      }, 500),
    );
  }

  validateMobileNumber(deps: CustomerLookupDeps) {
    if (deps.mobileNumber && typeof deps.mobileNumber === 'string') {
      const matchingCustomer = deps.customers.find(
        (c) => c.mobile_no === deps.mobileNumber,
      );

      if (!matchingCustomer) {
        // Don't clear customer name - allow new customer entry
        deps.setSelectedCustomer(null);

        this.formDataService.updateFormData({
          ...deps.formData,
          mobileNumber: deps.mobileNumber,
          empCode: deps.formData?.empCode || '',
          storeCode: deps.formData?.storeCode || '',
          currentDate: deps.formData?.currentDate || new Date(),
          customerName: deps.customerName || '',
          salesRep: deps.formData?.salesRep || '',
        });
      }
    } else if (!deps.mobileNumber) {
      // Only clear when mobile number is empty
      deps.setCustomerName('');
      deps.setSelectedCustomer(null);

      this.formDataService.updateFormData({
        ...deps.formData,
        mobileNumber: '',
        empCode: deps.formData?.empCode || '',
        storeCode: deps.formData?.storeCode || '',
        currentDate: deps.formData?.currentDate || new Date(),
        customerName: '',
        salesRep: deps.formData?.salesRep || '',
      });
    }
  }

  onMobileNumberInputBlur(deps: CustomerLookupDeps) {
    // Don't validate on blur - only validate when user is actively typing
    // This prevents clearing customer name when just tabbing through fields
    if (deps.mobileNumberValidationTimer) {
      clearTimeout(deps.mobileNumberValidationTimer);
    }
  }

  canProceedMobile(deps: CustomerLookupDeps): boolean {
    if (!deps.mobileNumber) {
      deps.setDisplayMobileNumberDialog(true);
      return false;
    }
    return true;
  }

  canProceedReceipt(deps: CustomerLookupDeps): boolean {
    if (
      deps.isMobileMandatory &&
      (!deps.mobileNumber || deps.mobileNumber.length !== 10)
    ) {
      deps.setDisplayHoldNameDialog(true);
      // setTimeout(() => { this.focusHoldReceiptNameInput(); }, 150);
      return false;
    }
    if (!deps.holdReceiptName) {
      deps.setDisplayHoldNameDialog(true);
      // setTimeout(() => { this.focusHoldReceiptNameInput(); }, 150);
      return false;
    }
    return true;
  }

  validateCustomerInfo(deps: CustomerLookupDeps): boolean {
    // Check if mobile number is mandatory based on client settings
    const isMobileMandatory = this.clientSettingsService.isCustomerMobileMandatory();
    deps.setIsMobileMandatory(isMobileMandatory);

    if (isMobileMandatory) {
      if (
        !deps.mobileNumber ||
        deps.mobileNumber.trim() === '' ||
        deps.mobileNumber.length !== 10
      ) {
        this.messageService.add({
          severity: 'error',
          summary: 'Mobile Number Required',
          detail: 'Please enter a valid 10-digit mobile number',
          life: 3000,
        });
        return false;
      }
    }

    // Customer name is optional - no validation needed
    return true;
  }

  // Ensure customers are loaded (on-demand)
  async ensureCustomersLoaded(deps: CustomerLookupDeps): Promise<void> {
    if (!this.authService.isAuthenticated()) {
      return;
    }

    if (deps.customers.length === 0 && !deps.loadingCustomers) {
      deps.setLoadingCustomers(true);
      try {
        deps.setCustomers(
          (await this.offerGroupService.getCustomers().toPromise()) || [],
        );
      } catch (error) {
        if (this.authService.isAuthenticated()) {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to load customer data',
            life: 3000,
          });
        }
      } finally {
        deps.setLoadingCustomers(false);
      }
    }
  }
}
